import Vue from "vue";
import Router from "vue-router";
import API from "@/api";
import admin from "./admin";
import guest from "./guest";
Vue.use(Router);

// Utility functions
const isLoggedIn = () => localStorage.getItem("isLoggedin") !== null;
const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
};

const getUserPermissions = () => {
  const storedPermissions = safeJsonParse(localStorage.getItem("userPermissions"), []);
  const user =
    safeJsonParse(localStorage.getItem("user"), null) ||
    safeJsonParse(localStorage.getItem("userDetails"), null);

  const userPermissions = Array.isArray(user && user.permissions)
    ? user.permissions
        .map((p) => (typeof p === "string" ? p : p && p.name))
        .filter(Boolean)
    : [];

  const permissions = Array.isArray(storedPermissions) ? storedPermissions : [];
  return [...new Set([...permissions, ...userPermissions])];
};
const getUserDetails = () => safeJsonParse(localStorage.getItem("userDetails"), null);
const getUserToken = () => localStorage.getItem("userLoginToken");

const loginCheck = (data, next, to) => {
  if (data.token) {
    localStorage.setItem("isLoggedin", data.token);
    localStorage.setItem("userPermissions", JSON.stringify(data.permission));
    Vue.prototype.$session.set("isLoggedin", data.token);
    Vue.prototype.$session.set("userPermissions", JSON.stringify(data.permission));

    // Redirect only if not already on the target route
    next(to.name === "" ? { name: "dashboard" } : { name: to.name });
  } else {
    next({ name: "home" });
  }
};

const redirectToLoginIfNeeded = (next, to) => {
  if (
    to.name !== "guest_login" &&
    to.name !== "forgot-password" &&
    to.name !== "reset-password" &&
    !isLoggedIn()
  ) {
    next({ name: "guest_login" });
  } else if (to.name === "guest_login" && isLoggedIn()) {
    next({ name: "dashboard" });
  } else {
    next();
  }
};

const checkPermissions = (next, to) => {
  const permissions = getUserPermissions();
  if (
    permissions.includes("super_admin") ||
    permissions.includes("property_owner")
  ) {
    // If the route path doesn't match a valid route, redirect to dashboard
    if (to.path == "/Admin" || to.path == "/Admin/") {
      next({ name: "dashboard" });
    } else {
      next();
    }
  } else {
    // () => next({ name: "login" })
    next({ name: "home" });
  }
};

const handleHomeRoutes = (next, to) => {
  const userToken = getUserToken();
  if (to.name === "guest_login" && userToken) {
    next({ name: "home" });
  } else if (to.name === "home" && !userToken) {
    next({ name: "guest_login" });
  } else if (to.name === "home/signup" && !userToken) {
    next({ name: "guest_signup" });
  } else {
    next();
  }
};

const handleAdminRoutes = (next, to) => {
  const email = localStorage.getItem("email");
  const password = localStorage.getItem("password");

  if (email && password && !isLoggedIn()) {
    const data = { email, password };
    API.login(
      data,
      (response) => loginCheck(response, next, to),
      () => next({ name: "home" })
    );
  } else if (
    to.name === "forget-password" &&
    to.name !== "reset-password" &&
    !isLoggedIn()
  ) {
    next({ name: "forget-password" });
  } else if (!isLoggedIn()) {
    // redirectToLoginIfNeeded(next, to);
    if (to.name === "dashboard") {
      next({ name: "guest_login" });
    } else {
      redirectToLoginIfNeeded(next, to);
    }
  } else {
    checkPermissions(next, to);
  }
};

const router = new Router({
  linkExactActiveClass: "active",
  scrollBehavior: () => ({ y: 0 }),
  mode: "history",
  base: "/",
  meta: { title: "Vanovy" },
  routes: [...admin, ...guest],
});

router.afterEach((to) => {
  Vue.nextTick(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: to.fullPath,
        page_title: document.title,
        page_location: window.location.href,
        send_to: 'G-4VR8KF4PZJ'
      });
    }
  });
});

router.onReady(() => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      send_to: 'G-4VR8KF4PZJ'
    });
  }
});

// router.beforeEach((to, from, next) => {
//   // Set document title
//   document.title = "Vanovy";

//   // Check authentication status
//   const isAuthenticatedToAccessWebsite = localStorage.getItem(
//     "isAuthenticatedToAccessWebsite"
//   );

//   if (
//     isAuthenticatedToAccessWebsite == "true"
//   ) {
//     // If user is authenticated, handle the route based on the path
//     if (to.path.startsWith("/Admin")) {
//       handleAdminRoutes(next, to);
//     } else if (to.path.startsWith("/dashboard") || to.path.startsWith("/edit-profile")) {
//       handleAdminRoutes(next, to);
//     }
//     // Handle home routes
//     else if (to.path.startsWith("/home")) {
//       handleHomeRoutes(next, to);
//     }
//     // For all other routes
//     else {
//       next();
//     }
//   } else {
//     if (to.name !== "websiteProtection" && to.path !== "/websiteProtection") {
//       console.log("User is not Authenticated to access the website");
//       next({ name: "websiteProtection" });
//     } else {
//       next(); // If already on the websiteProtection page, just continue
//     }
//   }
// });


router.beforeEach((to, from, next) => {
  document.title = "Vanovy";

    // 1. If user somehow hits 'websiteProtection', redirect them to home
    // since the site is now open to everyone.
    if (to.name === "websiteProtection") {
      return next({ name: "home" });
    }

  // =========================================================
  // PUBLIC ROUTES (access allowed without authentication)
  // =========================================================
  const publicRoutes = [
    '/login/google/callback',      // Google OAuth callback
    '/auth/facebook/callback',     // Facebook OAuth callback
    '/privacypolicy',
    '/termsofservice',
    '/websiteProtection',          // the password page itself
  ];

  const isPublic = publicRoutes.some(route =>
    to.path.startsWith(route) || to.path === route
  );

  if (isPublic) {
    return next();
  }

  // =========================================================
  // AUTHENTICATED USERS — HANDLE ADMIN / HOME ROUTES
  // =========================================================
  if (
    to.path.startsWith("/Admin") ||
    to.path.startsWith("/dashboard") ||
    to.path.startsWith("/edit-profile")
  ) {
    return handleAdminRoutes(next, to);
  }

  if (to.path.startsWith("/home")) {
    return handleHomeRoutes(next, to);
  }

  return next();
});

// router.beforeEach((to, from, next) => {
//   document.title = "Vanovy";
//   // Complete Public Access Routes (No website protection / no authentication required)
//   const publicRoutes = [
//     '/login/google/callback',
//     '/auth/facebook/callback',
//     '/privacypolicy',
//     '/termsofservice',
//     '/websiteProtection',
//     '/guest_login',
//     '/guest_signup'
//   ];

//   const isPublic = publicRoutes.some(route => to.path === route);

//   // Always allow callback routes & protection page access
//   if (isPublic) {
//     return next();
//   }

//   const isAuthenticatedToAccessWebsite = localStorage.getItem("isAuthenticatedToAccessWebsite");

//   if (isAuthenticatedToAccessWebsite === "true") {

//     // Admin-auth-required routes
//     if (
//       to.path.startsWith("/Admin") ||
//       to.path.startsWith("/dashboard") ||
//       to.path.startsWith("/edit-profile")
//     ) {
//       return handleAdminRoutes(next, to);
//     }

//     // Guest home routes
//     if (to.path.startsWith("/home")) {
//       return handleHomeRoutes(next, to);
//     }

//     return next(); // allow other routes

//   } else {
//     // Not authenticated to access website protection layer
//      if (to.name !== "websiteProtection") {
//       console.log("User blocked by Website Protection Layer");
//       return next({ name: "websiteProtection" });
//     }

//     return next();
//   }
// });


export default router;

